import { embeddedAppBuildMetadata } from './app-build-identity.ts';

const RECOVERY_QUERY_PARAMETER = '_fl_build_recovery';
const RECOVERY_STORAGE_KEY = 'forumandlex:stale-build-recovery';
const STALE_ASSET_FAILURE_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /loading(?: css)? chunk .+ failed/i,
  /unable to preload (?:css|script)/i,
];

export const currentAppBuildRecoveryId = [
  embeddedAppBuildMetadata.commitSha || 'unknown-commit',
  embeddedAppBuildMetadata.builtAt || 'unknown-build-time',
].join(':');

interface RecoveryStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => unknown;
  removeItem: (key: string) => unknown;
}

interface RecoveryLocation {
  href: string;
  replace: (url: string | URL) => unknown;
}

interface RecoveryHistory {
  state?: unknown;
  replaceState?: (data: unknown, unused: string, url?: string | URL | null) => unknown;
}

interface RecoveryEvent {
  payload?: unknown;
  preventDefault?: () => void;
}

export interface RecoveryBrowser {
  location?: RecoveryLocation;
  history?: RecoveryHistory;
  sessionStorage?: RecoveryStorage;
  addEventListener?: (type: string, listener: (event: RecoveryEvent) => void) => unknown;
  removeEventListener?: (type: string, listener: (event: RecoveryEvent) => void) => unknown;
}

interface RecoveryAttempt {
  buildId: string;
  attemptedAt?: unknown;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function nestedMessage(value: unknown): unknown {
  return objectValue(value)?.message;
}

function errorMessage(error: unknown): string {
  const candidate = objectValue(error);
  return String(
    nestedMessage(candidate?.payload) ||
      nestedMessage(candidate?.reason) ||
      candidate?.message ||
      error ||
      '',
  );
}

function sessionStorageFor(browser: RecoveryBrowser | null | undefined): RecoveryStorage | null {
  try {
    return browser?.sessionStorage || null;
  } catch {
    return null;
  }
}

function readRecoveryAttempt(storage: RecoveryStorage | null): RecoveryAttempt | null {
  try {
    const attempt: unknown = JSON.parse(storage?.getItem(RECOVERY_STORAGE_KEY) || 'null');
    const candidate = objectValue(attempt);
    return typeof candidate?.buildId === 'string'
      ? { buildId: candidate.buildId, attemptedAt: candidate.attemptedAt }
      : null;
  } catch {
    return null;
  }
}

function rememberRecoveryAttempt(
  storage: RecoveryStorage | null,
  buildId: string,
  attemptedAt: number,
): boolean {
  try {
    storage?.setItem(RECOVERY_STORAGE_KEY, JSON.stringify({ buildId, attemptedAt }));
    return readRecoveryAttempt(storage)?.buildId === buildId;
  } catch {
    return false;
  }
}

function forgetRecoveryAttempt(storage: RecoveryStorage | null, buildId: string): void {
  try {
    if (readRecoveryAttempt(storage)?.buildId === buildId) {
      storage?.removeItem(RECOVERY_STORAGE_KEY);
    }
  } catch {
    // A blocked storage API must not hide the original application error.
  }
}

function recoveryUrl(currentUrl: string | URL, buildId: string): URL {
  const url = new URL(currentUrl);
  url.searchParams.set(RECOVERY_QUERY_PARAMETER, buildId);
  return url;
}

export function isStaleBuildAssetError(error: unknown): boolean {
  const message = errorMessage(error);
  return (
    STALE_ASSET_FAILURE_PATTERNS.some((pattern) => pattern.test(message)) ||
    (/pdf\.worker/i.test(message) && /(?:404|failed|not found|network)/i.test(message))
  );
}

export function requestStaleBuildRecovery(
  error: unknown,
  {
    browser = globalThis.window,
    buildId = currentAppBuildRecoveryId,
    now = Date.now,
  }: {
    browser?: RecoveryBrowser;
    buildId?: string;
    now?: () => number;
  } = {},
): boolean {
  if (!browser?.location?.href || !isStaleBuildAssetError(error)) return false;

  let currentUrl: URL;
  try {
    currentUrl = new URL(browser.location.href);
  } catch {
    return false;
  }
  const sessionStorage = sessionStorageFor(browser);
  const previousAttempt = readRecoveryAttempt(sessionStorage);
  if (
    currentUrl.searchParams.get(RECOVERY_QUERY_PARAMETER) === buildId ||
    previousAttempt?.buildId === buildId
  )
    return false;

  rememberRecoveryAttempt(sessionStorage, buildId, now());
  try {
    browser.location.replace(recoveryUrl(currentUrl, buildId).href);
    return true;
  } catch {
    forgetRecoveryAttempt(sessionStorage, buildId);
    return false;
  }
}

export function reloadApplicationForLatestBuild({
  browser = globalThis.window,
  now = Date.now,
}: {
  browser?: RecoveryBrowser;
  now?: () => number;
} = {}): boolean {
  if (!browser?.location?.href) return false;
  const url = recoveryUrl(browser.location.href, `manual:${now()}`);
  try {
    browser.location.replace(url.href);
    return true;
  } catch {
    return false;
  }
}

export function removeStaleBuildRecoveryParameter(
  browser: RecoveryBrowser = globalThis.window,
): boolean {
  if (!browser?.location?.href || !browser?.history?.replaceState) return false;
  try {
    const url = new URL(browser.location.href);
    if (!url.searchParams.has(RECOVERY_QUERY_PARAMETER)) return false;

    url.searchParams.delete(RECOVERY_QUERY_PARAMETER);
    browser.history.replaceState(
      browser.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
    return true;
  } catch {
    return false;
  }
}

function removeRecoveryParameterWhenLoopSafe(browser: RecoveryBrowser): void {
  const locationHref = browser.location?.href;
  if (!locationHref) return;
  let url: URL;
  try {
    url = new URL(locationHref);
  } catch {
    return;
  }
  const recoveryBuildId = url.searchParams.get(RECOVERY_QUERY_PARAMETER);
  if (!recoveryBuildId) return;

  const rememberedBuildId = readRecoveryAttempt(sessionStorageFor(browser))?.buildId;
  if (rememberedBuildId !== recoveryBuildId && !recoveryBuildId.startsWith('manual:')) return;

  removeStaleBuildRecoveryParameter(browser);
}

export function installStaleBuildRecovery(
  browser: RecoveryBrowser = globalThis.window,
): () => void {
  if (!browser?.addEventListener) return () => {};

  const handlePreloadError = (event: RecoveryEvent) => {
    const recoveryStarted = requestStaleBuildRecovery(
      event?.payload || new Error('Failed to fetch dynamically imported module'),
      { browser },
    );
    if (recoveryStarted) event.preventDefault?.();
  };

  browser.addEventListener('vite:preloadError', handlePreloadError);
  removeRecoveryParameterWhenLoopSafe(browser);

  return () => {
    browser.removeEventListener?.('vite:preloadError', handlePreloadError);
  };
}
